// Sessão (SPEC §6.2): quem está logado, login, troca de senha e saída. Compartilhado por aluno e painel.
// Primeiro acesso por código é do app do aluno (api/aluno).
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  EntrarAlunoCorpo,
  EntrarEquipeCorpo,
  Papel,
  Sessao,
  TrocarSenhaCorpo,
} from "@grindset/server/contratos";
import { comAcademiaDoApp } from "@plataforma/academia";
import { api, ErroApi } from "./cliente";
import { useMutacao } from "./mutacao";
import { limparTreinoAoSair, sessaoTrocou } from "./aluno/treinoLocal";

export const CHAVE_SESSAO = ["sessao"] as const;

/** Quem está logado: academia + usuário (`null` sem sessão). */
const identidade = (s: Sessao | null | undefined) => (s ? `${s.academia.id}:${s.usuario.id}` : null);

/**
 * A sessão que voltou do servidor é de outra pessoa (ou de outra academia) que a do cache? Acontece sem passar pelo
 * login DESTA aba: no navegador, outra aba saiu e entrou como B e o cookie mudou por baixo (AppSec C1, 02/10). Sem
 * sessão no cache ainda (`undefined`, primeira consulta) não é troca.
 */
export function sessaoMudouDeDono(anterior: Sessao | null | undefined, nova: Sessao | null): boolean {
  return anterior !== undefined && identidade(anterior) !== identidade(nova);
}

/** Consulta da sessão (`useSessao`), separada para o teste da troca por fora. */
export async function consultarSessao(qc: QueryClient): Promise<Sessao | null> {
  let nova: Sessao | null;
  try {
    nova = await api.get<Sessao>("/api/auth/sessao", { semRedirecionar: true });
  } catch (e) {
    if (!(e instanceof ErroApi && (e.status === 401 || e.status === 404))) throw e;
    nova = null;
  }
  if (sessaoMudouDeDono(qc.getQueryData<Sessao | null>(CHAVE_SESSAO), nova)) {
    sessaoTrocou();
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== CHAVE_SESSAO[0] });
  }
  return nova;
}

/**
 * Sessão atual ou `null` (sem login). Nunca redireciona: os guardas de rota decidem. Se o dono mudou por fora
 * (`sessaoMudouDeDono`), faz o mesmo que o login desta aba: nenhum envio de treino em andamento continua
 * (`sessaoTrocou`) e o cache da outra pessoa sai. No web a consulta se refaz ao voltar para a aba (é aí que a troca
 * feita na outra aba aparece); no app há uma sessão só por aparelho.
 */
export function useSessao() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: CHAVE_SESSAO,
    queryFn: () => consultarSessao(qc),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: __ALVO__ === "nativo" ? true : "always",
  });
}

/** Área inicial de cada papel. */
export const inicioDoPapel = (papel: Papel): string => (papel === "ALUNO" ? "/" : "/painel");

/** Allowlist explícita dos papéis da equipe (nunca "tudo que não é ALUNO"). */
const PAPEIS_EQUIPE: readonly Papel[] = ["PROFESSOR", "GERENTE", "MASTER", "PROFESSOR_LIDER", "RECEPCAO"];
export const ehEquipe = (papel: Papel | undefined): boolean => !!papel && PAPEIS_EQUIPE.includes(papel);

/** Fuso da academia logada (padrão America/Sao_Paulo). */
export function useFuso(): string {
  return useSessao().data?.academia.fuso ?? "America/Sao_Paulo";
}

function useGravarSessao() {
  const qc = useQueryClient();
  return (sessao: Sessao) => {
    // troca de usuário: descarta cache de outra pessoa
    // e nenhum envio de treino em andamento continua com a credencial nova (treinoLocal.ts)
    sessaoTrocou();
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== CHAVE_SESSAO[0] });
    qc.setQueryData(CHAVE_SESSAO, sessao);
  };
}

export function useEntrarAluno() {
  const gravar = useGravarSessao();
  return useMutacao({
    mutationFn: (corpo: EntrarAlunoCorpo) =>
      api.post<Sessao>("/api/auth/aluno/entrar", __ALVO__ === "nativo" ? comAcademiaDoApp(corpo) : corpo, {
        semRedirecionar: true,
      }),
    onSuccess: gravar,
  });
}

export function useEntrarEquipe() {
  const gravar = useGravarSessao();
  return useMutacao({
    mutationFn: (corpo: EntrarEquipeCorpo) =>
      api.post<Sessao>("/api/auth/equipe/entrar", corpo, { semRedirecionar: true }),
    onSuccess: gravar,
  });
}

export function useTrocarSenha() {
  const qc = useQueryClient();
  return useMutacao({
    mutationFn: (corpo: TrocarSenhaCorpo) =>
      api.post<Sessao>("/api/auth/senha", corpo, { semRedirecionar: true }),
    onSuccess: (sessao) => qc.setQueryData(CHAVE_SESSAO, sessao),
  });
}

/** Sai e limpa todo o cache do usuário. */
export function useSair() {
  const qc = useQueryClient();
  return useMutacao({
    // "always": sair não pode ficar pausado sem rede (o cache é limpo de qualquer jeito)
    networkMode: "always",
    mutationFn: () => {
      // Web: desliga o "treinando agora" ainda com o cookie e tira da fila o que passou de 7 dias. No app quem faz
      // isso é o `aoEsquecerSessao` (plataforma/nativo/entrada.ts), que também cobre a sessão morta (401).
      const papel = qc.getQueryData<Sessao | null>(CHAVE_SESSAO)?.usuario.papel;
      if (__ALVO__ !== "nativo" && papel === "ALUNO") limparTreinoAoSair();
      return api.post("/api/auth/sair", undefined, { semRedirecionar: true });
    },
    onSettled: () => {
      qc.clear();
      qc.setQueryData(CHAVE_SESSAO, null);
    },
  });
}

/** Grava uma sessão recebida por outro fluxo (ex.: primeiro acesso do aluno). */
export function useDefinirSessao() {
  return useGravarSessao();
}
